import ReactApexChart from "react-apexcharts";

const CHART_COLORS = [
  "#3d8ecc",
  "#2ecc71",
  "#f39c12",
  "#9b59b6",
  "#e74c3c",
  "#1abc9c",
  "#e67e22"
];

function formatAxisValue(value) {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
  return value;
}

const cardStyle = {
  background: "#fff",
  borderRadius: "8px",
  border: "1px solid #e8e8e8",
  padding: "16px 16px 4px",
  marginBottom: "20px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
};

export default function LineAnalysisChart({ title, data, yAxisLabel, variant = "area" }) {
  const noData = !data || !data.brands || !data.data || !data.data.length;

  if (noData) {
    return (
      <div style={cardStyle}>
        <h6 style={{ color: "#3d8ecc", fontWeight: 600, marginBottom: "8px" }}>{title}</h6>
        <p style={{ margin: 0, color: "#aaa" }}>No data available</p>
      </div>
    );
  }

  const xKey = Object.keys(data.data[0])[0];
  const categories = data.data.map(d => {
    const val = d[xKey];
    const date = new Date(val);
    return isNaN(date) ? val : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  });

  const series = data.brands.map(brand => ({
    name: brand,
    data: data.data.map(d => d[brand] ?? 0)
  }));

  // ─── STACKED 100% — brand share over time ────────────────────────────────
  if (variant === "stacked") {
    const options = {
      chart: {
        type: "area",
        stacked: true,
        stackType: "100%",
        toolbar: { show: true, tools: { download: true, zoom: false, pan: false, reset: true } },
        animations: { enabled: true, speed: 700 },
        fontFamily: "inherit"
      },
      colors: CHART_COLORS,
      dataLabels: { enabled: false },
      stroke: { curve: "smooth", width: 1.5 },
      fill: { type: "solid", opacity: 0.8 },
      xaxis: {
        categories,
        labels: { rotate: -30, style: { fontSize: "11px", colors: "#888" } },
        axisBorder: { color: "#e8e8e8" },
        axisTicks: { color: "#e8e8e8" }
      },
      yaxis: {
        title: { text: "% Share", style: { fontSize: "12px", color: "#888", fontWeight: 500 } },
        labels: {
          formatter: val => `${val != null ? val.toFixed(0) : 0}%`,
          style: { colors: "#888" }
        },
        max: 100
      },
      grid: { borderColor: "#f0f0f0", strokeDashArray: 4 },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: val => `${val != null ? val.toFixed(1) : 0}%` }
      },
      legend: { position: "bottom", horizontalAlign: "center", fontSize: "12px" }
    };

    return (
      <div style={cardStyle}>
        <h6 style={{ color: "#3d8ecc", fontWeight: 600, fontSize: "14px", marginBottom: "2px" }}>{title}</h6>
        <p style={{ color: "#999", fontSize: "12px", margin: "0 0 4px" }}>
          Relative brand share (%) over time
        </p>
        <ReactApexChart options={options} series={series} type="area" height={300} />
      </div>
    );
  }

  // ─── DEFAULT: gradient area chart ────────────────────────────────────────
  const options = {
    chart: {
      type: "area",
      toolbar: {
        show: true,
        tools: { download: true, zoom: true, zoomin: true, zoomout: true, pan: true, reset: true }
      },
      zoom: { enabled: true, type: "x" },
      animations: { enabled: true, easing: "easeinout", speed: 700 },
      fontFamily: "inherit"
    },
    colors: CHART_COLORS,
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 2.5 },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.03,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      categories,
      labels: { rotate: -30, style: { fontSize: "11px", colors: "#888" } },
      axisBorder: { color: "#e8e8e8" },
      axisTicks: { color: "#e8e8e8" }
    },
    yaxis: {
      title: { text: yAxisLabel, style: { fontSize: "12px", color: "#888", fontWeight: 500 } },
      labels: { formatter: formatAxisValue, style: { colors: "#888" } }
    },
    grid: { borderColor: "#f0f0f0", strokeDashArray: 4 },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: val => val != null ? val.toLocaleString() : val }
    },
    legend: { position: "bottom", horizontalAlign: "center", fontSize: "12px" },
    markers: { size: 0, hover: { size: 5 } }
  };

  return (
    <div style={cardStyle}>
      <h6 style={{ color: "#3d8ecc", fontWeight: 600, fontSize: "14px", marginBottom: "2px" }}>{title}</h6>
      <p style={{ color: "#999", fontSize: "12px", margin: "0 0 4px" }}>{yAxisLabel} over time</p>
      <ReactApexChart options={options} series={series} type="area" height={340} />
    </div>
  );
}
